import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { 
  Play,
  Palette, 
  LayoutGrid, 
  FileCheck, 
  SendHorizonal,
  ArrowLeft,
  ArrowRight,
  Lock,
  AlertCircle,
  PenTool
} from 'lucide-react';

const steps = [
  { 
    id: 'cinematic',
    icon: Play,
    label: 'Welcome',
    color: 'from-gold/80 to-gold'
  },
  { 
    id: 'material-selection',
    icon: Palette,
    label: 'Material',
    color: 'from-gold to-gold-dark'
  },
  { 
    id: 'purchase-path',
    icon: LayoutGrid,
    label: 'Path',
    color: 'from-gold-dark to-gold'
  },
  { 
    id: 'customization-type',
    icon: PenTool,
    label: 'Type',
    color: 'from-gold to-gold-dark'
  },
  { 
    id: 'customize',
    icon: PenTool,
    label: 'Design',
    color: 'from-gold-dark to-gold'
  },
  { 
    id: 'inquiry',
    icon: FileCheck,
    label: 'Quote',
    color: 'from-gold to-gold-dark'
  },
  { 
    id: 'confirmation',
    icon: SendHorizonal,
    label: 'Complete',
    color: 'from-gold to-gold-dark'
  }
];

export const ProgressBar: React.FC = () => {
  const { currentStep, setStep, visitedSteps, canProceedToStep } = useCustomizationStore();
  const currentIndex = steps.findIndex(step => step.id === currentStep);
  const progress = (currentIndex / (steps.length - 1)) * 100;

  const handleNavigation = (direction: 'back' | 'forward') => {
    const newIndex = direction === 'back' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < steps.length) {
      const targetStep = steps[newIndex].id;
      if (direction === 'back' || canProceedToStep(targetStep)) {
        setStep(targetStep);
      }
    }
  };

  const isDarkTheme = currentStep === 'material-selection' || currentStep === 'confirmation';

  return (
    <div className={`w-full ${isDarkTheme ? 'bg-[#1C1C1C]' : 'bg-cream'} progress-bar`}>
      <div className="w-full max-w-6xl mx-auto px-4 py-6">
        <div className="relative">
          {/* Navigation Buttons */}
          <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between pointer-events-none z-20">
            <AnimatePresence>
              {currentIndex > 0 && (
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  onClick={() => handleNavigation('back')}
                  className={`pointer-events-auto w-10 h-10 flex items-center justify-center rounded-full shadow-lg text-gold hover:text-gold-dark transition-colors -translate-x-5 ${
                    isDarkTheme ? 'bg-white/10 border border-gold/20' : 'bg-white'
                  }`}
                >
                  <ArrowLeft className="w-5 h-5" />
                </motion.button>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {currentIndex < steps.length - 1 && (
                <motion.button
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  onClick={() => handleNavigation('forward')}
                  className={`pointer-events-auto w-10 h-10 flex items-center justify-center rounded-full shadow-lg transition-colors translate-x-5 ${
                    canProceedToStep(steps[currentIndex + 1].id)
                      ? `text-gold hover:text-gold-dark cursor-pointer ${
                          isDarkTheme ? 'bg-white/10 border border-gold/20' : 'bg-white'
                        }`
                      : 'text-gray-400 cursor-not-allowed bg-white/5'
                  }`}
                  title={!canProceedToStep(steps[currentIndex + 1].id) ? 'Complete current step to proceed' : ''}
                >
                  {canProceedToStep(steps[currentIndex + 1].id) ? (
                    <ArrowRight className="w-5 h-5" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          {/* Progress Line */}
          <div className={`absolute top-1/2 left-0 w-full h-1 -translate-y-1/2 rounded-full overflow-hidden ${
            isDarkTheme ? 'bg-white/10' : 'bg-gold/10'
          }`}>
            <motion.div
              className="h-full bg-gradient-to-r from-gold to-gold-dark"
              initial={{ width: '0%' }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>

          {/* Steps */}
          <div className="relative flex justify-between items-center">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const isLocked = !canProceedToStep(step.id) && index > currentIndex;

              return (
                <div key={step.id} className="flex flex-col items-center">
                  <motion.div
                    className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full ${
                      isLocked
                        ? isDarkTheme ? 'bg-white/10 border border-white/20' : 'bg-gold/10'
                        : isCompleted || isCurrent
                        ? 'bg-gradient-to-r ' + step.color
                        : isDarkTheme ? 'bg-white/10 border border-white/20' : 'bg-gold/10'
                    } ${isCurrent ? 'ring-4 ring-gold/20' : ''}`}
                    animate={{
                      scale: isCurrent ? 1.2 : 1,
                      transition: { type: 'spring', stiffness: 300, damping: 30 }
                    }}
                  >
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={isCompleted ? 'check' : isLocked ? 'lock' : step.id}
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.8, opacity: 0 }}
                      >
                        {isCompleted ? (
                          <ArrowRight className="w-5 h-5 text-white" />
                        ) : isLocked ? (
                          <Lock className={`w-5 h-5 ${isDarkTheme ? 'text-white/40' : 'text-gold/40'}`} />
                        ) : (
                          <Icon className={`w-5 h-5 ${
                            isCurrent 
                              ? 'text-white' 
                              : isDarkTheme 
                                ? 'text-white/60' 
                                : 'text-gold/60'
                          }`} />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>

                  <motion.span
                    className={`absolute mt-12 text-sm font-medium text-center w-20 ${
                      isLocked
                        ? isDarkTheme ? 'text-white/40' : 'text-gold/40'
                        : isCurrent
                        ? isDarkTheme ? 'text-white' : 'text-gold'
                        : isDarkTheme ? 'text-white/60' : 'text-gold/60'
                    }`}
                    animate={{
                      scale: isCurrent ? 1.1 : 1,
                      transition: { type: 'spring', stiffness: 300, damping: 30 }
                    }}
                  >
                    {step.label}
                  </motion.span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};